import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import formStyles from 'pages/Form.module.css';
import styles from 'pages/EditGroup.module.css';
import { CreateOrganizationDocument, UpdateOrganizationDocument, CreateImageUploadDocument } from 'graphql/mutations.generated';
import { GetOrganizationDetailDocument, GetOrganizationDetailQuery } from 'graphql/queries.generated';
import { useMutation, useQuery } from '@apollo/client/react';
import ImageUpload from 'components/ImageUpload';
import { ImageOwnerType, ImageSize } from 'app-constants';
import globalStyles from 'Global.module.css';

const EditGroup: React.FC = () => {
    const { organizationId } = useParams<{ organizationId?: string }>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [urlName, setUrlName] = useState('');
  const [region, setRegion] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');
    const [pendingImageUpload, setPendingImageUpload] = useState<{
        uploadInput: any;
        file: File;
    } | null>(null);
  const navigate = useNavigate();

    const { data, loading: queryLoading } = useQuery<GetOrganizationDetailQuery>(GetOrganizationDetailDocument, {
        variables: {
            organizationId: organizationId || ''
        },
        skip: !organizationId
    });

    const [createOrganization, { loading: createLoading }] = useMutation(CreateOrganizationDocument);
    const [updateOrganization, { loading: updateLoading }] = useMutation(UpdateOrganizationDocument);
    const [createImageUpload] = useMutation(CreateImageUploadDocument);

    const loading = createLoading || updateLoading;

    useEffect(() => {
        if (data?.organization) {
            const org = data.organization;
            setName(org.name);
            setDescription(org.description || '');
            setUrlName(org.urlName || '');
            setRegion(org.region);
            setTags(org.tags.filter((tag): tag is string => tag !== null).join(', '));
        }
    }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');

    try {
        let imageIdToUse: string | undefined = data?.organization?.imageId || undefined;

        // Handle image upload if pending
        if (pendingImageUpload) {
            const uploadResult = await createImageUpload({
                variables: {
                    ownerId: pendingImageUpload.uploadInput.ownerId,
                    ownerType: pendingImageUpload.uploadInput.ownerType,
                    contentType: pendingImageUpload.uploadInput.contentType,
                    crop_left: pendingImageUpload.uploadInput.crop_left,
                    crop_top: pendingImageUpload.uploadInput.crop_top,
                    crop_width: pendingImageUpload.uploadInput.crop_width,
                    crop_height: pendingImageUpload.uploadInput.crop_height,
                },
            });

            if (uploadResult.data?.createImageUpload) {
                const { imageId, uploadURL } = uploadResult.data.createImageUpload;

                // Upload the file to S3
                await fetch(uploadURL, {
                    method: 'PUT',
                    body: pendingImageUpload.file,
                });

                imageIdToUse = imageId;
            }
        }

        if (organizationId) {
            const result = await updateOrganization({
                variables: {
                    organizationId,
                    name,
                    description: description || undefined,
                    urlName: urlName || undefined,
                    region,
                    tags: tagsArray,
                    imageId: imageIdToUse,
                },
            });

            if (result.data?.updateOrganization) {
                navigate(`/groups/${organizationId}`);
            }
        } else {
            const result = await createOrganization({
                variables: {
                    name,
                    description: description || undefined,
                    urlName: urlName || undefined,
                    region,
                    tags: tagsArray,
                    imageId: imageIdToUse,
                },
            });

            if (result.data?.createOrganization) {
                navigate(`/groups/${result.data.createOrganization}`);
            }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

    if (queryLoading) {
        return (
            <div className={`${formStyles.formContainer} ${styles.editGroup}`}>
                <p>Loading group details...</p>
            </div>
        );
    }

    return (
        <div className={`${formStyles.formContainer} ${styles.editGroup}`}>
            <h1>{organizationId ? 'Edit Group' : 'Create Group'}</h1>
      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name">Group Name:</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </div>
        <div>
          <label htmlFor="urlName">URL Name:</label>
          <input
            type="text"
            id="urlName"
            value={urlName}
            onChange={(e) => setUrlName(e.target.value)}
            placeholder="e.g., my-awesome-group"
          />
        </div>
        <div>
          <label htmlFor="region">Region:</label>
          <input
            type="text"
            id="region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="tags">Tags (comma-separated):</label>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., technology, meetup, networking"
            required
          />
        </div>
                {organizationId && (
                    <div>
                        <label>Group Image:</label>
                        <ImageUpload
                            ownerType={ImageOwnerType.Organization}
                            displaySize={ImageSize.Card}
                            imageId={data?.organization?.imageId}
                            ownerId={organizationId}
                            onImageChange={setPendingImageUpload}
                            aspectRatio={16 / 9}
                        />
                    </div>
                )}
        {error && <div className={formStyles.error}>{error}</div>}
                <button type="submit" disabled={loading} className={globalStyles.submitButton}>
                    {loading ? (organizationId ? 'Updating group...' : 'Creating group...') : (organizationId ? 'Update Group' : 'Create Group')}
        </button>
      </form>
    </div>
  );
};

export default EditGroup;

